// Phone numbers and email addresses in a job's notes, made tappable where the notes are
// shown (design manager, 2 Nov 2026). Display only: the stored note never changes, and
// only the Notes field is ever scanned. The same rules as the iPhone (NoteLinkDetector):
// a strict UK matcher (07, 01/02/03, 0800, +44 with an optional (0); 10 or 11 digits;
// spaces, brackets, hyphens and dots allowed), so a mileage, a registration or a claim
// number starting 05 never matches; and email addresses (mailto).

export interface NotePart {
  text: string;
  href?: string;
}

const PHONE_CANDIDATE = /(?<![\w+])(?:\+44[ .\-]?(?:\(0\)[ .\-]?)?|\(?0)\d[\d ().\-]{7,15}\d(?!\w)/g;
const EMAIL = /(?<![\w.+-])[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(?![\w-])/g;

/** 07 and 03 numbers have 11 digits; 01 and 02 have 10 or 11; 0800 has 10 or 11. */
export function isUKNumber(digits: string): boolean {
  if (!/^\d+$/.test(digits)) return false;
  if (digits.startsWith('0800')) return digits.length === 10 || digits.length === 11;
  if (digits.startsWith('07') || digits.startsWith('03')) return digits.length === 11;
  if (digits.startsWith('01') || digits.startsWith('02')) return digits.length === 10 || digits.length === 11;
  return false;
}

/** tel: for a candidate that is a real UK number, else null. */
export function telHref(candidate: string): string | null {
  let digits = candidate.replace(/\D/g, '');
  if (candidate.startsWith('+44')) {
    digits = digits.slice(2);
    if (candidate.includes('(0)')) digits = digits.slice(1);
    digits = `0${digits}`;
  }
  return isUKNumber(digits) ? `tel:${digits}` : null;
}

/** The note split into plain text and links, in order. */
export function splitNoteLinks(text: string): NotePart[] {
  const found: { start: number; end: number; href: string }[] = [];
  // Keep whole email addresses intact even when their local part is a UK number.
  for (const match of text.matchAll(EMAIL)) {
    const start = match.index!;
    const end = start + match[0].length;
    found.push({ start, end, href: `mailto:${match[0]}` });
  }
  const urls = [...text.matchAll(/(?:https?:\/\/|www\.)[^\s<>]+/gi)]
    .map(match => ({ start: match.index!, end: match.index! + match[0].length }));
  for (const match of text.matchAll(PHONE_CANDIDATE)) {
    const start = match.index!;
    const end = start + match[0].length;
    if ([...found, ...urls].some(link => start < link.end && end > link.start)) continue;
    const href = telHref(match[0]);
    if (href) found.push({ start, end, href });
  }
  found.sort((a, b) => a.start - b.start);

  const parts: NotePart[] = [];
  let at = 0;
  for (const link of found) {
    if (link.start > at) parts.push({ text: text.slice(at, link.start) });
    parts.push({ text: text.slice(link.start, link.end), href: link.href });
    at = link.end;
  }
  if (at < text.length) parts.push({ text: text.slice(at) });
  return parts;
}
